import { useState, useEffect, useCallback } from 'react';
import { Search, Forward, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ChatAvatar } from './ChatAvatar';
import type { TelegramChat, ForwardOptions } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ForwardMessageModalProps {
  open: boolean;
  onClose: () => void;
  messageIds: number[];
  fromChatId: number;
  onForward: (messageIds: number[], toChatId: number, options?: ForwardOptions) => Promise<unknown>;
}

export function ForwardMessageModal({
  open,
  onClose,
  messageIds,
  fromChatId,
  onForward,
}: ForwardMessageModalProps) {
  const [search, setSearch] = useState('');
  const [chats, setChats] = useState<TelegramChat[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isForwarding, setIsForwarding] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);

  // Forward options
  const [hideAuthor, setHideAuthor] = useState(false);
  const [hideCaptions, setHideCaptions] = useState(false);

  // Fetch chats
  useEffect(() => {
    if (!open) {return;}

    const fetchChats = async () => {
      setIsLoading(true);
      try {
        const queryString = new URLSearchParams({
          limit: '50',
          offset: '0',
          ...(search && { search }),
        }).toString();

        const response = await fetch(`${API_URL}/api/telegram-client/chats?${queryString}`);

        const result: ApiResponse<TelegramChat[]> = await response.json();
        if (result.success && result.data) {
          // Exclude current chat
          setChats(result.data?.filter(c => c.id !== fromChatId) ?? []);
        }
      } catch {
        // Ignore
      } finally {
        setIsLoading(false);
      }
    };

    fetchChats();
  }, [open, search, fromChatId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedChatId(null);
      setHideAuthor(false);
      setHideCaptions(false);
    }
  }, [open]);

  const handleForward = useCallback(async () => {
    if (!selectedChatId || messageIds.length === 0) {return;}

    setIsForwarding(true);
    try {
      const options: ForwardOptions = {};
      if (hideAuthor) {options.noAuthors = true;}
      if (hideCaptions) {options.noCaptions = true;}

      await onForward(messageIds, selectedChatId, options);
      onClose();
    } finally {
      setIsForwarding(false);
    }
  }, [selectedChatId, messageIds, hideAuthor, hideCaptions, onForward, onClose]);

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) {return '';}
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Forward className="h-5 w-5" />
            Переслать {messageIds.length > 1 ? `${messageIds.length} сообщений` : 'сообщение'}
          </DialogTitle>
          <DialogDescription>
            Выберите чат для пересылки
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск чатов..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Chat list */}
          <ScrollArea className="h-[250px] -mx-2 px-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (chats.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <p className="text-sm">
                  {search ? 'Чаты не найдены' : 'Нет доступных чатов'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={cn(
                      'flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors',
                      selectedChatId === chat.id
                        ? 'bg-primary/10 border border-primary/30'
                        : 'hover:bg-accent'
                    )}
                    onClick={() => setSelectedChatId(chat.id)}
                  >
                    <ChatAvatar chatId={chat.id} title={chat.title} className="h-10 w-10" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{chat.title}</div>
                      {chat.lastMessage && (
                        <div className="text-xs text-muted-foreground truncate">
                          {chat.lastMessage}
                        </div>
                      )}
                    </div>
                    {chat.lastMessageDate && (
                      <div className="text-xs text-muted-foreground">
                        {formatDate(chat.lastMessageDate)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </ScrollArea>

          {/* Options */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-author"
                checked={hideAuthor}
                onCheckedChange={(checked) => setHideAuthor(checked === true)}
              />
              <Label htmlFor="hide-author" className="text-sm cursor-pointer">
                Скрыть имя отправителя
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hide-captions"
                checked={hideCaptions}
                onCheckedChange={(checked) => setHideCaptions(checked === true)}
              />
              <Label htmlFor="hide-captions" className="text-sm cursor-pointer">
                Скрыть подписи к медиа
              </Label>
            </div>
          </div>

          {/* Forward button */}
          <Button
            className="w-full"
            onClick={handleForward}
            disabled={!selectedChatId || isForwarding}
          >
            {isForwarding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Пересылка...
              </>
            ) : (
              <>
                <Forward className="mr-2 h-4 w-4" />
                Переслать
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
